import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Truck, Boxes, Warehouse, Plane, Ship, Clock3, MapPin, Shield, Zap, Users, BarChart3, CheckCircle2 } from 'lucide-react';
import { NavLink } from 'react-router-dom';

const services = [
  { 
    icon: Boxes, 
    title: 'CROSSDOCS', 
    desc: 'Inbound to outbound in under 6 hours with minimal storage and handling.',
    features: ['Same-day processing', 'Real-time visibility', 'Quality control', 'Custom labeling'],
    pricing: 'From $45/pallet',
    image: 'assets/warehouse-8921538_1280.jpg',
    benefits: ['Reduce storage costs', 'Faster delivery times', 'Improved inventory turnover']
  },
  { 
    icon: Truck, 
    title: 'Line‑Haul', 
    desc: 'Regional and long‑haul routes with strict SLAs and live tracking.',
    features: ['Dedicated capacity', 'GPS tracking', 'Temperature control', 'Insurance included'],
    pricing: 'From $1.25/mile',
    image: 'assets/truck-1910871_1280.jpg',
    benefits: ['Predictable pricing', 'Reliable delivery', 'Full transparency']
  },
  { 
    icon: Warehouse, 
    title: 'Warehousing', 
    desc: 'Short‑term buffer storage and inventory services near key hubs.',
    features: ['Climate control', 'Security monitoring', 'Inventory management', 'Pick & pack'],
    pricing: 'From $0.85/sq ft/month',
    image: 'assets/warehouse-9006780_1280.jpg',
    benefits: ['Strategic locations', 'Flexible terms', 'Integrated systems']
  },
  { 
    icon: Ship, 
    title: 'Ocean & Drayage', 
    desc: 'Port pickup, container deconsolidation and FCL/LCL optimization.',
    features: ['Port drayage', 'Container deconsolidation', 'Customs clearance', 'FCL/LCL handling'],
    pricing: 'From $350/container',
    image: 'assets/ship-105596_1280.jpg',
    benefits: ['Port expertise', 'Cost optimization', 'Time savings']
  },
  { 
    icon: Plane, 
    title: 'Air & Expedited', 
    desc: 'Next‑flight‑out and time‑critical shipments with priority handling.',
    features: ['Next-flight-out', 'Priority handling', 'Real-time updates', 'White-glove service'],
    pricing: 'From $2.50/lb',
    image: 'assets/airplane-4974678_1280.jpg',
    benefits: ['Speed guarantee', 'Global reach', 'Premium service']
  },
  { 
    icon: Clock3, 
    title: 'Last‑Mile & White‑Glove', 
    desc: 'Time‑window deliveries, appointment scheduling and inside delivery.',
    features: ['Appointment scheduling', 'Inside delivery', 'Assembly services', 'Returns handling'],
    pricing: 'From $85/delivery',
    image: 'assets/container-789488_1280.jpg',
    benefits: ['Customer satisfaction', 'Reduced returns', 'Brand protection']
  },
];

const additionalServices = [
  {
    title: 'Supply Chain Consulting',
    desc: 'Expert analysis and optimization recommendations for your logistics operations.',
    icon: BarChart3,
    pricing: 'Custom quote'
  },
  {
    title: 'Technology Integration',
    desc: 'Seamless integration with your existing systems and real-time data feeds.',
    icon: Zap,
    pricing: 'Custom quote'
  },
  {
    title: 'Risk Management',
    desc: 'Comprehensive insurance coverage and proactive claims handling.',
    icon: Shield,
    pricing: 'Included with service'
  }
];

export default function Services() {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Services — CROSSDOCS</title>
        <meta name="description" content="Comprehensive logistics services including CROSSDOCS, line-haul, warehousing, ocean freight, air cargo, and last-mile delivery." />
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              End-to-End Logistics Solutions
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-8">
              Our platform blends a curated carrier network with smart cross‑dock hubs and software, 
              so you move freight faster with less risk and more control.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <NavLink 
                to="/contact" 
                className="inline-flex items-center rounded-xl bg-primary-600 px-8 py-4 text-lg font-semibold text-white shadow-glow hover:bg-primary-700 hover:shadow-glow hover:scale-105 transition-all duration-300"
              >
                Get a Quote
              </NavLink>
              <NavLink 
                to="/pricing" 
                className="inline-flex items-center rounded-xl px-8 py-4 text-lg font-semibold text-white ring-2 ring-white/30 hover:ring-white/50 hover:bg-white/10 transition-all duration-300"
              >
                View Pricing
              </NavLink>
            </div>
          </motion.div>
        </div>
        
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="assets/port-6670684_1280.jpg" 
            alt="Port Operations"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
      </section>

      {/* Main Services Grid */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Core Services
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              From CROSSDOCS to last-mile delivery, we provide comprehensive logistics services
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative bg-white rounded-2xl shadow-premium hover:shadow-premium-lg transition-all duration-500 hover:-translate-y-2 overflow-hidden"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  <div className="absolute top-6 right-6 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                    {service.pricing}
                  </div>
                </div>
                
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                      <service.icon className="h-6 w-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-slate-900">{service.title}</h3>
                      <p className="text-slate-600">{service.desc}</p>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-slate-900 mb-3">Key Features:</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="h-4 w-4 text-primary-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-slate-900 mb-3">Benefits:</h4>
                    <ul className="space-y-2">
                      {service.benefits.map((benefit) => (
                        <li key={benefit} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="h-4 w-4 text-primary-500" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <NavLink 
                    to="/contact" 
                    className="inline-flex items-center justify-center w-full rounded-xl bg-primary-600 px-6 py-3 text-white font-semibold hover:bg-primary-700 transition-colors duration-300"
                  >
                    Get Quote for {service.title}
                  </NavLink>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Value-Added Services
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Beyond our core logistics services, we offer specialized solutions to optimize 
              your entire supply chain operation.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-premium hover:shadow-premium-lg transition-all duration-300 hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mb-6">
                  <service.icon className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600 mb-4">{service.desc}</p>
                <div className="text-primary-600 font-semibold">{service.pricing}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Coverage Map */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Nationwide Coverage
              </h2>
              <p className="text-xl text-slate-600 mb-8">
                Our strategic network of CROSSDOCS hubs, warehouses, and carrier partners 
                ensures comprehensive coverage across all 48 contiguous states.
              </p>
              
              <div className="space-y-6">
                {[
                  { region: 'West Coast', hubs: 'Los Angeles, Seattle, Portland', coverage: '100%' },
                  { region: 'Southwest', hubs: 'Dallas, Houston, Phoenix', coverage: '100%' },
                  { region: 'Midwest', hubs: 'Chicago, Detroit, Minneapolis', coverage: '100%' },
                  { region: 'Northeast', hubs: 'New York, Boston, Philadelphia', coverage: '100%' },
                  { region: 'Southeast', hubs: 'Atlanta, Miami, Charlotte', coverage: '100%' },
                ].map((region, index) => (
                  <motion.div
                    key={region.region}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                  >
                    <div>
                      <h3 className="font-semibold text-slate-900">{region.region}</h3>
                      <p className="text-sm text-slate-600">{region.hubs}</p>
                    </div>
                    <div className="text-primary-600 font-bold">{region.coverage}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-premium-lg">
                <img 
                  src="assets/container-4197259_1280.jpg" 
                  alt="Container Operations"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/80 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-2xl font-bold mb-3">Strategic Infrastructure</h3>
                  <p className="text-lg">Optimized locations for maximum efficiency and minimal transit times</p>
                </div>
              </div>
              
              {/* Floating stats */}
              <div className="absolute -top-6 -right-6 bg-white rounded-2xl p-6 shadow-premium">
                <div className="text-3xl font-bold text-primary-600">48</div>
                <div className="text-sm text-slate-600">States Covered</div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-premium">
                <div className="text-3xl font-bold text-primary-600">25+</div>
                <div className="text-sm text-slate-600">Hub Locations</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Optimize Your Logistics?
            </h2>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto mb-8">
              Get a customized quote for your specific logistics needs. Our experts will analyze 
              your requirements and provide a solution that fits your business perfectly.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <NavLink 
                to="/contact" 
                className="inline-flex items-center rounded-xl bg-white px-8 py-4 text-lg font-semibold text-primary-700 shadow-premium hover:shadow-premium-lg hover:scale-105 transition-all duration-300"
              >
                Get Custom Quote
              </NavLink>
              <NavLink 
                to="/contact" 
                className="inline-flex items-center rounded-xl px-8 py-4 text-lg font-semibold text-white ring-2 ring-white/30 hover:ring-white/50 hover:bg-white/10 transition-all duration-300"
              >
                Schedule Consultation
              </NavLink>
            </div>
            <div className="mt-8 text-primary-100">
              <p className="text-sm">✓ Free consultation • ✓ Custom solutions • ✓ 24/7 support</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}






